/**
 * Area Listings Page V2 Controller
 *
 * Follows the Controller/View pattern used in the codebase.
 * Controller handles: hooks, data derivation, handlers with analytics.
 * View handles: pure render, JSX composition.
 *
 * This controller adds analytics tracking for mobile search components.
 */

import { useCallback } from 'react';
import { AnalyticsController } from 'analytics/analytics-controller';
import {
  GTM_MOBILE_FILTER_BAR_ALL_FILTERS_CLICK,
  GTM_MOBILE_FILTER_BAR_SORT_CHANGED,
  GTM_MOBILE_FILTER_BAR_SAVE_SEARCH_CLICK,
} from 'constants/events';
import AreaListingsPageV2View from './area-listings-page-v2-view';
import type { AreaListingsPageV2ViewProps } from './area-listings-page-v2-view';
import type { Sort } from 'contexts/preferences/listing-params/types';

export type AreaListingsPageV2ControllerProps = AreaListingsPageV2ViewProps;

/**
 * AreaListingsPageV2Controller
 *
 * Handles all hooks, data derivation, and event handlers with analytics.
 * Renders the pure AreaListingsPageV2View with data and handlers.
 */
export function AreaListingsPageV2Controller(props: AreaListingsPageV2ControllerProps) {
  // Analytics handlers for MobileListingFilterBarV2
  const handleFilterButtonClickAnalytics = useCallback(() => {
    AnalyticsController.trackEvent(
      GTM_MOBILE_FILTER_BAR_ALL_FILTERS_CLICK,
      'Search Filters'
    );
  }, []);

  const handleSortChangedAnalytics = useCallback((sort: Sort) => {
    AnalyticsController.trackEvent(
      GTM_MOBILE_FILTER_BAR_SORT_CHANGED,
      'Search Filters',
      sort
    );
  }, []);

  const handleSaveSearchButtonClickAnalytics = useCallback(() => {
    AnalyticsController.trackEvent(
      GTM_MOBILE_FILTER_BAR_SAVE_SEARCH_CLICK,
      'User Interaction'
    );
  }, []);

  return (
    <AreaListingsPageV2View
      {...props}
      onFilterButtonClickAnalytics={handleFilterButtonClickAnalytics}
      onSortChangedAnalytics={handleSortChangedAnalytics}
      onSaveSearchButtonClickAnalytics={handleSaveSearchButtonClickAnalytics}
    />
  );
}

export default AreaListingsPageV2Controller;
